import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import AuthCard from '../components/AuthCard';
import Button from '../components/Button';
import CodeInput, { CODE_LENGTH } from '../components/CodeInput';
import FormBanner from '../components/FormBanner';
import FullScreenSpinner from '../components/FullScreenSpinner';
import { useAuth } from '../auth/AuthContext';
import { errorMessage } from '../lib/errors';

// Server refusal codes → copy. The server keeps its wording deliberately generic
// (it must not confirm whether an address has an account), so the friendlier
// phrasing belongs here, where the distinction is already safe to make.
const CODE_ERRORS: Record<string, string> = {
  INVALID_CODE: "That code isn't right. Check the email and try again.",
  CODE_EXPIRED: 'That code has expired. Request a new one below.',
  TOO_MANY_ATTEMPTS: 'Too many attempts. Request a new code to continue.',
  ALREADY_VERIFIED: 'This email is already verified. You can sign in.',
};

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function VerifyEmailPage() {
  const { me, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Where the user was originally headed. Carried through the verification step
  // so a deep link still lands where it meant to.
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const [email, setEmail] = useState<string | null>(null);
  const [booting, setBooting] = useState(true);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [refocus, setRefocus] = useState(0);
  // Guards the auto-submit: without it, React 18 StrictMode's double-invoke (and
  // any re-render while a request is in flight) can fire a second verify with the
  // same code, burning an attempt off the user's budget for free.
  const inFlight = useRef(false);

  // The pending token is httpOnly, so the address has to come from the server.
  // This is also what makes a page refresh survivable.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const pending = await api.pendingVerification();
        if (cancelled) return;
        setEmail(pending.email);
        setCooldown(pending.retryAfterSeconds);
      } catch {
        // No pending token and no session ⇒ there is nothing to verify here.
        if (!cancelled) setEmail(null);
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Resend countdown.
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const submit = useCallback(
    async (submitted: string) => {
      if (inFlight.current || submitted.length !== CODE_LENGTH) return;
      inFlight.current = true;
      setError(null);
      setSubmitting(true);
      try {
        await api.verifyEmail({ code: submitted });
        // The server issued the real session; sync `me` so the guards let us past.
        await refresh();
        navigate(from, { replace: true });
      } catch (err) {
        const code = err instanceof ApiError ? err.code : undefined;
        setError(
          (code && CODE_ERRORS[code]) ||
            errorMessage(err, "That code isn't right. Check the email and try again."),
        );
        // Clear and refocus so the next attempt starts clean, exactly as the
        // login form clears a rejected password.
        setCode('');
        setRefocus((n) => n + 1);
      } finally {
        inFlight.current = false;
        setSubmitting(false);
      }
    },
    [from, navigate, refresh],
  );

  async function onResend() {
    setError(null);
    try {
      const res = await api.resendVerification();
      setCooldown(res.retryAfterSeconds);
      setCode('');
    } catch (err) {
      setError(errorMessage(err, 'Could not send a new code. Please try again.'));
    }
  }

  if (booting) return <FullScreenSpinner label="Loading" />;
  // Already verified (or signed in and done) — never show this screen to them.
  if (me?.email_verified) return <Navigate to={from} replace />;
  // Nothing pending and no session: the flow has to start from sign up.
  if (!email) return <Navigate to="/login" replace />;

  return (
    <AuthCard
      eyebrow="One more step"
      title="Check your email"
      // Hedged deliberately. This screen is also shown for an address that
      // already has a VERIFIED account, where no code was sent — the decoy that
      // stops register revealing which addresses exist. Stating "we sent a code"
      // there is simply false, and it strands the one person most likely to see
      // it: someone who forgot they already had an account. The conditional
      // wording is true in every branch and identical in all of them, so it
      // corrects the lie without leaking what the decoy hides. The Sign in link
      // in the footer is the way out.
      subtitle={`If that address still needs verifying, we've sent a 6-digit code to ${email}`}
      footer={
        // The sign-in link is not decoration. Registering an address that already
        // has a verified account is answered exactly like a new signup — that is
        // the anti-enumeration decoy — so a real person who forgot they had an
        // account lands here waiting for a code that will never arrive. Without a
        // way back to sign in, this screen is a dead end for them.
        <>
          <span className="block">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-primary hover:opacity-80">
              Sign in
            </Link>
          </span>
          <span className="mt-1 block">
            Wrong address?{' '}
            <Link to="/register" className="font-medium text-primary hover:opacity-80">
              Use a different email
            </Link>
          </span>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void submit(code);
        }}
        noValidate
      >
        {/* The id is what aria-describedby on the input points at; FormBanner
            already carries role="alert" so it is announced on appearance. */}
        {error ? (
          <div id="code-error">
            <FormBanner kind="error">{error}</FormBanner>
          </div>
        ) : null}

        <label className="block" htmlFor="code">
          <span className="mb-1 block text-sm font-medium text-[var(--cad-text-muted,#d4d4d8)]">
            Verification code
          </span>
          <CodeInput
            value={code}
            onChange={setCode}
            onComplete={(next) => void submit(next)}
            disabled={submitting}
            error={Boolean(error)}
            describedBy={error ? 'code-error' : undefined}
            refocusToken={refocus}
          />
        </label>

        <Button
          type="submit"
          loading={submitting}
          disabled={code.length !== CODE_LENGTH}
          className="h-11 w-full text-base"
        >
          Verify email
        </Button>
      </form>

      <div className="text-center text-sm text-muted-foreground">
        {cooldown > 0 ? (
          <span aria-live="polite">Resend in {formatCountdown(cooldown)}</span>
        ) : (
          <button
            type="button"
            onClick={() => void onResend()}
            className="font-medium text-primary hover:opacity-80"
          >
            Didn&rsquo;t get it? Resend code
          </button>
        )}
      </div>
    </AuthCard>
  );
}
